"use client";

import React, { useState, useEffect } from "react";

interface MCPToken {
  id: string;
  name: string;
  role: string;
  org_id?: string;
  brand_id?: string;
  scopes: string[];
  rate_limit: number;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

interface MCPTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MCPTokensModal({ isOpen, onClose }: MCPTokensModalProps) {
  const [tokens, setTokens] = useState<MCPToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [selectedRole, setSelectedRole] = useState<string>("user");
  const [newRawToken, setNewRawToken] = useState<string | null>(null);
  const [newRawTokenRole, setNewRawTokenRole] = useState<string>("user");
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"cursor" | "claude">("cursor");
  const [origin, setOrigin] = useState<string>("https://platform.setapi.in");

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.origin) {
      setOrigin(window.location.origin);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadTokens();
    }
  }, [isOpen]);

  const loadTokens = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/mcp/tokens");
      const data = await res.json();
      if (data.tokens) {
        setTokens(data.tokens);
      }
    } catch (e) {
      console.error("Failed to load MCP tokens:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const name = tokenName.trim() || `Client-${new Date().toLocaleDateString("en-IN")}`;
    setCreating(true);
    try {
      const res = await fetch("/api/mcp/tokens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, role: selectedRole }),
      });
      const data = await res.json();
      if (data.token) {
        setNewRawToken(data.token);
        setNewRawTokenRole(data.role || selectedRole);
        setTokenName("");
        loadTokens();
      }
    } catch (e) {
      console.error("Failed to generate MCP token:", e);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm("Are you sure you want to revoke this MCP token? External AI clients using it will lose access immediately.")) return;
    setRevokingId(id);
    try {
      const res = await fetch(`/api/mcp/tokens?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setTokens((prev) => prev.filter((t) => t.id !== id));
      }
    } catch (e) {
      console.error("Failed to revoke token:", e);
    } finally {
      setRevokingId(null);
    }
  };

  const copyToClipboard = (text: string, isConfig = false) => {
    navigator.clipboard.writeText(text);
    if (isConfig) {
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 2000);
    } else {
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  if (!isOpen) return null;

  const currentTokenPlaceholder = newRawToken || "mcp_YOUR_TOKEN_HERE";
  const sseUrl = `${origin}/mcp/v1/sse`;

  const cursorConfigJson = JSON.stringify(
    {
      mcpServers: {
        setapi: {
          url: sseUrl,
          headers: {
            Authorization: `Bearer ${currentTokenPlaceholder}`,
          },
        },
      },
    },
    null,
    2
  );

  const claudeConfigJson = JSON.stringify(
    {
      mcpServers: {
        setapi: {
          url: sseUrl,
          headers: {
            Authorization: `Bearer ${currentTokenPlaceholder}`,
          },
        },
      },
    },
    null,
    2
  );

  const displayedConfigJson = activeTab === "cursor" ? cursorConfigJson : claudeConfigJson;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div 
        className="w-full max-w-2xl bg-[#0e1117] border border-[#232734] rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        style={{ borderRadius: "8px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#232734] bg-[#141822]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[15px] font-semibold text-white">Model Context Protocol (MCP) Operator</h3>
                <span className="px-2 py-0.5 text-[10px] font-mono font-medium rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                  SSE Online
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Autonomous User-Proxy via <span className="font-mono text-emerald-400/90">{origin}/mcp/v1/sse</span> with 3-Tier Multi-Tenant RBAC.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-sm">
          {/* Newly Generated Raw Token Banner */}
          {newRawToken && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold tracking-wide uppercase text-amber-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  Your New MCP Token (Copy Now — Shown Once)
                </span>
                <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 uppercase">
                  Role: {newRawTokenRole}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 rounded bg-black/40 border border-amber-500/20 font-mono text-xs text-amber-100 select-all overflow-x-auto">
                  {newRawToken}
                </code>
                <button
                  onClick={() => copyToClipboard(newRawToken)}
                  className="px-3 py-2 rounded bg-amber-500 text-black font-semibold text-xs hover:bg-amber-400 transition-colors shrink-0 flex items-center gap-1.5"
                >
                  {copiedToken ? "Copied!" : "Copy Token"}
                </button>
              </div>
              <p className="text-[11px] text-amber-300/70">
                This token authenticates external AI tools with strict tenant scoping. It will not be shown again.
              </p>
            </div>
          )}

          {/* Generator Input & Role Selector */}
          <div className="space-y-3 p-4 rounded-lg bg-[#141822] border border-[#232734]">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Generate Scoped Client Token
              </h4>
              <span className="text-[11px] text-gray-500">3-Tier Multi-Tenant RBAC</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pb-1">
              <label 
                className={`flex flex-col p-2.5 rounded-lg border cursor-pointer transition-all ${
                  selectedRole === "user" 
                    ? "bg-indigo-600/15 border-indigo-500 text-white" 
                    : "bg-[#0b0d13] border-[#232734] text-gray-400 hover:border-gray-600"
                }`}
                onClick={() => setSelectedRole("user")}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs text-white">Trader / User</span>
                  <input type="radio" checked={selectedRole === "user"} onChange={() => setSelectedRole("user")} className="accent-indigo-500" />
                </div>
                <span className="text-[10px] text-gray-400 mt-1">Personal strategies, backtests, personal deployments & PnL</span>
              </label>

              <label 
                className={`flex flex-col p-2.5 rounded-lg border cursor-pointer transition-all ${
                  selectedRole === "org_admin" 
                    ? "bg-amber-600/15 border-amber-500 text-white" 
                    : "bg-[#0b0d13] border-[#232734] text-gray-400 hover:border-gray-600"
                }`}
                onClick={() => setSelectedRole("org_admin")}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs text-white">Org Admin</span>
                  <input type="radio" checked={selectedRole === "org_admin"} onChange={() => setSelectedRole("org_admin")} className="accent-amber-500" />
                </div>
                <span className="text-[10px] text-gray-400 mt-1">Org-wide oversight, shared vault search, member risk caps</span>
              </label>

              <label 
                className={`flex flex-col p-2.5 rounded-lg border cursor-pointer transition-all ${
                  selectedRole === "brand_admin" 
                    ? "bg-purple-600/15 border-purple-500 text-white" 
                    : "bg-[#0b0d13] border-[#232734] text-gray-400 hover:border-gray-600"
                }`}
                onClick={() => setSelectedRole("brand_admin")}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs text-white">Brand Admin</span>
                  <input type="radio" checked={selectedRole === "brand_admin"} onChange={() => setSelectedRole("brand_admin")} className="accent-purple-500" />
                </div>
                <span className="text-[10px] text-gray-400 mt-1">Brand health telemetry, risk parameters, platform monitoring</span>
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Client name (e.g. Cursor IDE, Claude Desktop, Antigravity CLI)"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                className="flex-1 px-3.5 py-2 rounded bg-[#0b0d13] border border-[#2a2f42] text-white text-xs placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
              />
              <button
                onClick={handleGenerate}
                disabled={creating}
                className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 text-white font-medium text-xs transition-colors shrink-0 disabled:opacity-50 flex items-center gap-2"
              >
                {creating ? "Generating..." : "Generate Token"}
              </button>
            </div>
          </div>

          {/* Quick Setup Guide / Config Snippets */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Client Configuration Snippet
              </h4>
              <div className="flex items-center gap-1 bg-[#141822] p-0.5 rounded border border-[#232734] text-xs">
                <button
                  onClick={() => setActiveTab("cursor")}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    activeTab === "cursor" ? "bg-indigo-600 text-white font-medium" : "text-gray-400 hover:text-white"
                  }`}
                >
                  Cursor IDE
                </button>
                <button
                  onClick={() => setActiveTab("claude")}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    activeTab === "claude" ? "bg-indigo-600 text-white font-medium" : "text-gray-400 hover:text-white"
                  }`}
                >
                  Claude Desktop
                </button>
              </div>
            </div>

            <div className="relative">
              <pre className="p-3.5 rounded bg-[#0b0d13] border border-[#232734] font-mono text-xs text-gray-300 overflow-x-auto leading-relaxed">
                {displayedConfigJson}
              </pre>
              <button
                onClick={() => copyToClipboard(displayedConfigJson, true)}
                className="absolute top-2.5 right-2.5 px-2.5 py-1 rounded bg-[#202534] hover:bg-[#2c3347] text-gray-200 text-xs transition-colors border border-[#373f55] flex items-center gap-1.5"
              >
                {copiedConfig ? "Copied!" : "Copy JSON"}
              </button>
            </div>
            <p className="text-[11px] text-gray-400">
              Paste this into your <code className="text-gray-300">.cursor/mcp.json</code> or Claude Desktop settings. Tools are filtered dynamically based on the token&apos;s role scope.
            </p>
          </div>

          {/* Active Tokens Table */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                Active MCP Tokens ({tokens.length})
              </h4>
              <button
                onClick={loadTokens}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-center py-6 text-gray-500 text-xs">Loading active tokens...</div>
            ) : tokens.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-xs border border-dashed border-[#232734] rounded-lg">
                No MCP tokens generated yet. Click &quot;Generate Token&quot; above.
              </div>
            ) : (
              <div className="border border-[#232734] rounded-lg overflow-hidden divide-y divide-[#232734] bg-[#141822]">
                {tokens.map((tok) => (
                  <div key={tok.id} className="flex items-center justify-between p-3 text-xs">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{tok.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase font-semibold ${
                          tok.role === 'brand_admin' 
                            ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30' 
                            : tok.role === 'org_admin' 
                            ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' 
                            : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                        }`}>
                          {tok.role.replace('_', ' ')}
                        </span>
                        <span className="px-1.5 py-0.2 rounded text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-mono">
                          {tok.rate_limit || 60} req/min
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-400 flex items-center gap-2">
                        <span>Created: {new Date(tok.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                        {tok.last_used_at && (
                          <span>· Last used: {new Date(tok.last_used_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                        )}
                        {tok.org_id && (
                          <span className="font-mono text-[10px] text-gray-500">Org: {tok.org_id.substring(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(tok.id)}
                      disabled={revokingId === tok.id}
                      className="px-2.5 py-1 text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded transition-colors border border-rose-500/20"
                    >
                      {revokingId === tok.id ? "Revoking..." : "Revoke"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3.5 bg-[#141822] border-t border-[#232734] flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span>Multi-Tenant Guarded Execution: Strict SQL query isolation · Financial mutations require HMAC 2-step confirmation</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded bg-[#202534] hover:bg-[#2c3347] text-white font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
